import React, { useEffect } from "react";
import { useSurveyStore } from "../store/useSurveyStore";
import apiClient from "../api/apiClient";
import { CircularProgress } from "@mui/material";
import { mixpanelStart } from "@/utils/analytics";
import { isMockMode, mockSurveyData } from "@/utils/mockSurvey";

const Loading = ({ onComplete }) => {
	const {
		setUserId,
		setSurveyId,
		setToken,
		setComplianceText,
		setQuestions,
		setInitTimestamp,
		setErrorCode,
		setSurveyTitle,
		setSurveySubTitle,
		setSurveyIntroTextHtml,
	} = useSurveyStore();

	useEffect(() => {
		// get token from #token=ABC123, but fallback to ?token=ABC123
		// prefer #token=ABC123 to prevent log and referral headers
		const tokenValue =
			new URLSearchParams(window.location.hash.slice(1)).get("token") || new URLSearchParams(window.location.search).get("token");

		// Mock mode: skip API call entirely, load fixture data
		if (isMockMode()) {
			setToken("MOCK_TOKEN");
			setQuestions(mockSurveyData.questions);
			if (mockSurveyData.survey_title) setSurveyTitle(mockSurveyData.survey_title);
			if (mockSurveyData.survey_sub_title) setSurveySubTitle(mockSurveyData.survey_sub_title);
			if (mockSurveyData.survey_intro_text_html) setSurveyIntroTextHtml(mockSurveyData.survey_intro_text_html);
			setUserId(mockSurveyData.user_id);
			setSurveyId(mockSurveyData.survey_id);
			const mysqlTimestamp = new Date().toISOString().slice(0, 19).replace("T", " ");
			setInitTimestamp(mysqlTimestamp);
			if (mockSurveyData.require_compliance === 1) {
				setComplianceText(mockSurveyData.compliance_text);
				onComplete("compliance");
			} else {
				onComplete("survey");
			}
			return;
		}

		if (!tokenValue) {
			// Set specific error code for missing token
			console.error("Missing Auth Token");
			setErrorCode("missing_required_vars");
			onComplete("invalid");
			return;
		}

		setToken(tokenValue);

		apiClient
			.post("/research/init", { token: tokenValue })
			.then((res) => {
				if (res.data.status !== "success") {
					// If there's an error code in the response, it will be captured by the interceptor
					onComplete("error");
					return;
				}

				// Store the questions data
				setQuestions(res.data.data.questions);

				// Store the survey title, subtitle, and intro text
				if (res?.data?.data?.survey_title) setSurveyTitle(res.data.data.survey_title);
				if (res?.data?.data?.survey_sub_title) setSurveySubTitle(res.data.data.survey_sub_title);
				if (res?.data?.data?.survey_intro_text_html) setSurveyIntroTextHtml(res.data.data.survey_intro_text_html);
				setUserId(res?.data?.data?.user_id);
				setSurveyId(res?.data?.data?.survey_id);

				// Start mixpanel
				mixpanelStart(res?.data?.data);

				// Store the init timestamp in MySQL format (UTC)
				const mysqlTimestamp = new Date().toISOString().slice(0, 19).replace("T", " ");

				setInitTimestamp(mysqlTimestamp);

				// Check if compliance is required
				if (res?.data?.data?.require_compliance === 1) {
					// Store compliance text and go to compliance step
					setComplianceText(res.data.data.compliance_text);
					onComplete("compliance");
				} else {
					// Skip compliance and go directly to survey
					onComplete("survey");
				}
			})
			.catch((error) => {
				console.error("Error initializing survey:", error);
				onComplete("error");
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [onComplete]);

	return (
		<div className="flex justify-center items-center min-h-screen">
			<CircularProgress />
		</div>
	);
};

export default Loading;
